<template>
    <div class="help">
        <h2>EscapeItKit Help</h2>

        <section>
            <h3>Escape It Director</h3>
            <p>The EscapeItKit Director is an essential part of most kits.  Put simply the Director is the
                control panel for your Escape Rooms.  Once you build a kit in the Kit Builder you use the Director
                to setup and run your Escape Room. We suggest using an iPad, other tablet, or smart phone to access the Director
                so you can control the room while observing and otherwise assisting participants.</p>

                <p>The Director's main menu has tabs for "Rooms", "Displays",
                "Interacts" and the "Timer".</p>

            <ul>
                <li><i class="far fa-door-closed"></i>
                    The "Rooms" tab is your list of rooms you are controlling. The Director is powerful enough to
                    manage multiple rooms simultaneously if you wish.  This is helpful when you have a large group
                    of people and the ability to setup multiple rooms.
                </li>
                <li><i class="far fa-tv"></i>
                    The "Displays" tab is where you connect and control display devices. More on this later.
                </li>
                <li><i class="far fa-tablet"></i>
                    The "Interacts" tab will list interact devices accessing your room. More on this later.
                </li>
                <li><i class="far fa-stopwatch"></i>
                    The "Timer" tab give you advanced control over the timer for the selected room.  If you want
                    to grant participants more time you can do so.  Depending on the group, often they would rather
                    win than fail and you can accommodate that if time is short.
                </li>
            </ul>

            <p>
                There is also a set of sub-tabs available when you have a room selected:
            </p>

            <ul>
                <li><i class="fas fa-bullhorn"></i> The "Direct" tab is the main control area for your room.  It
                will consist of several large "buttons" that will directly trigger events, sounds, change screens,
                etc.  More on this later.</li>
                <li><i class="fas fa-parachute-box"></i> The "Assist" tab will give you clues, hints, and solutions
                specific to your kit build.</li>
                <li><i class="fas fa-question-square"></i> The "Help" tab is this document which will have both
                EscapeItKit help and kit specific help for your kit build.</li>
            </ul>

        </section>

        <section>
            <h3>Creating a New Room</h3>
            <p>A "Room" is the actual experience you have setup for your kit build.  After you have created
            kit build you can attach it to a new room.  You can make as many rooms as you need from the same
            or different kit builds.</p>

            <ul>
                <li>In the Escape It Director and the "Rooms" tab from the main menu choose the blue
                    <i class="fas fa-plus-square"></i> Plus Button in the header.</li>
                <li>Select the Kit build you wish to use for this room.</li>
                <li>You can name your room anything you wish.  By default it will be names the same
                as the kit build you chose.</li>
                <li>Click on "Create" and your new room will be added to the "Rooms" tab.</li>
            </ul>
        </section>

        <section>
            <h3>Display Devices</h3>
            <p>A "Display" is an important part of your Escape Room.  It's a special website that you can visit
                on nearly any device such as a laptop, iPad or other tablets, etc.  Once connected it will react
                based on the progress they make while escaping the room as well as provide the countdown timer,
                background audio, visual and audio effects and often be part of puzzles.  It's not meant to be
                intractable, as in participants shouldn't be allowed to use it, only view it.</p>
        </section>

        <section>
            <h3>Choosing a Display Device</h3>
            <p>As mentioned the Display can be set up on any device that supports a modern web browser.  Ideally,
                you can hook up a laptop or tablet to a TV or even a projector but it's not required.  Because the
                Display uses a special website to operate it's designed to be presented in full-screen so only the
                escape room material is visible.  Using a device that allows this is perfect.</p>

            <p>The other requirement you should consider when choosing a device is how to integrate audio.  Naturally,
                you can use the standard audio from the device but if you can output it to a larger speaker system,
                either wired or Bluetooth, you can greatly enhance the room's atmosphere and experience.</p>

            <h6><i class="fas fa-laptop"></i> Using an Computer or Laptop:</h6>
            <ul>
                <li>Using Google Chrome (Recommended) browse to https://app.escapeitkit.com/#/display.</li>
                <li>
                    Switch to Full Screen:
                    <ul>
                        <li>On Windows press F11 on the keyboard. If you use a laptop or similar device with the Fn
                            key on the keyboard, you may have to press Fn + F11.</li>
                        <li>On MacOS click the green circle in the top left corner of Chrome, choose View > Enter
                            Full Screen from the menu bar or use the keyboard shortcut Control + Command + F.  If the
                            URL or Bookmark bar is still visible check settings under the View menu bar.</li>
                    </ul>
                </li>
                <li>If for some reason the view isn't properly fitting your screen use Control/Command +/- to change
                    the zoom level for the page.  Control/Command 0 (zero) will reset the zoom level to normal.</li>
                <li>Click on "Get Code".</li>
                <li>Connect the Display in Director.</li>
            </ul>

            <h6><i class="far fa-tablet"></i> Using an iPad</h6>
            <ul>
                <li>Using Safari (Recommended) browse to https://app.escapeitkit.com/#/display.</li>
                <li>
                    To get the Full Screen effect you must save the site to your Home Screen:
                    <ul>
                        <li>Tap the Share/Export icon (similar to <i class="far fa-external-link"></i>)</li>
                        <li>Select the "<i class="fas fa-plus-square"></i> Add to Home Screen" icon.</li>
                        <li>Give it a name if you wish and click "Add"</li>

                    </ul>
                </li>
                <li>Launch the Display site from your new "App" on your Home Screen.  This should open with only the
                    top menu showing.</li>
                <li>Tap on "Get Code".</li>
                <li>Connect the Display in Director.</li>
            </ul>
        </section>

        <section>
            <h3>Connecting a Display</h3>
            <p>Using a modern web browser simply go to the site:

                <div class="url">https://app.escapeitkit.com/#/display</div>

            <p>This will load the special website with a four character code with the note "Connect Display in Director".
                If you refresh the page you will get a new code.  If possible, be sure to set the browser to present
                the site in full-screen.</p>

            <p>On another device such as your phone or tablet, access the EscapeItKit Director app (accessible
                from https://app.escapeitkit.com/#/login) and navigate to the room you wish to manage.  Tap the
                "Displays" icon and then "Connect a Display".  Provide the four character code and a custom display
                name and click "Connect".  The display should change to either a blank or another starting screen
                once connected.</p>

            <p>You don't have to stop there.  If you have the equipment you can connect as many displays as you wish.</p>
        </section>

        <section>
            <h3>Controlling the Display</h3>
            <p>Once started the Display will automatically react to the progress participates make in the room as
                they try to escape and solve different puzzles.  However, depending on the kit, there are other
                events you can trigger for the Display.  Some are these are just for ambiance but others are
                essential for completing the room.</p>

            <p>Controlling the Display is done from the Director app.  If you set up more than one you can individually
                control each.  You have the power to design your room how you wish!</p>

            <h3>Troubleshooting the Display</h3>
            <p>Setting up the Display should be fairly straightforward but if you are having problems and need to start
                from scratch you may need to forcefully disconnect a display.  To do this, on the Display device,
                navigate to the https://app.escapeitkit.com/#/display/disconnect and then back to
                https://app.escapeitkit.com/#/display.</p>

            <p>If the Display is not connecting or not providing a four character code it's possible the device you are
                using is not compatible.  Be sure the browser and the operating system is up to date.  If you are still
                having problems please reach out to us at support@escapeitkit.com.</p>
        </section>

        <section>
                <h3>Interact Devices</h3>
                <p>Another important part of your Escape Room will be "Interact Devices".  These are devices that participants
                will be encouraged and often required to use to complete a room.  They can be any device with a modern web browser such
                as a smart phone, iPad or other tablet, laptop, etc.</p>
                <p>Each room will be different on how they use an Interact Device however they will all end up accessing it the
                same way.  A special web address (Similar to http://escit.tk/1234) will be provided to participants.  This is often
                done with a display device or other printed prop.  Alternatively you can also provide the interact device already
                setup with the proper configuration.</p>
            </section>
    </div>
</template>

<script>
    export default {
        name: "help"
    }
</script>

<style lang="scss" scoped>
    .help {
        font-family: "Roboto", sans-serif;
        line-height: 1.9rem;
        padding: 25px 40px 0 40px !important;
        font-size: 0.9rem;

        section {
            margin-bottom: 3rem;
        }

        h2 {
            font-family: 'Sumana', serif;
            padding-bottom: 8px;
        }

        h3 {
            font-family: 'Sumana', serif;

            font-size: 1.5rem;
            border-bottom: 1px solid #E46A3B;
            padding-bottom: 8px;
            margin-bottom: 1.4rem;

            i {
                width: 30px;
                margin-right: 0.5rem;
            }
        }

        p {
            margin-bottom: 1.5rem;
        }

        h6 i {
            margin-right: 6px;
        }

        p, td, li {
            i {
                margin: 0 4px;
            }
        }

        ol li, ul li {
            margin-bottom: 15px;
            line-height: normal;
        }

        .url {
            border: 1px solid #017BFF;
            padding: 10px 20px;
            font-size: 20px;
            margin-bottom: 30px;
            display: inline-block;
        }
    }

</style>


// WEBPACK FOOTER //
// src/components/app/director/help.vue